import React, { useState, useEffect } from 'react';
import { CheckCircle, Lock, Edit, Send, Music, Star, User, Calendar, MessageSquare, FileText, AlertTriangle, Copy } from 'lucide-react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { addLyricsComment, approveLyrics, getLyrics } from '../../redux/reduxSlices/ordersSlice';
import LyricsUploadPopup from './LyricsUploadPopup';
import toast from 'react-hot-toast';
import moment from "moment";
import digikoreLogo from "../../assets/digikorestudios.png";


const ShareLyrics = () => {
    const params = useParams();
    const orderId = params.id;
    const [newComment, setNewComment] = useState('');
    const [activeCommentBox, setActiveCommentBox] = useState(null);
    const [isEditingLyrics, setIsEditingLyrics] = useState(false);
    const [lyricsForEditing, setLyricsForEditing] = useState();
    const [uploadLyricsPopup, setUploadLyricsPopup] = useState(false);
    const [confirmationPopup, setConfirmationPopup] = useState({
        show: false,
        lyricId: null,
        lyricData: null
    });

    const dispatch = useDispatch();
    const { lyricsData = {} } = useSelector((state) => state.orders);

    useEffect(() => {
        if (orderId) {
            dispatch(getLyrics(orderId));
        }
    }, [orderId]);

    const items = lyricsData?.items || [];
    const approved_lyric_id = lyricsData?.approved_lyric_id || null;
    const locked = lyricsData?.locked || false;

    const handleSubmitComment = async (lyricId) => {
        if (!newComment.trim()) return;
        try {
            const payload = {
                "order_id": orderId,
                "lyric_id": lyricId,
                "comment": newComment.trim()
            }
            const response = await dispatch(addLyricsComment(payload)).unwrap()
            if (response.success) {
                await dispatch(getLyrics(orderId));
                toast.success('Comment added!', {
                    duration: 2000,
                    position: 'top-right',
                });
                setNewComment('');
                setActiveCommentBox(null);
            }
        } catch (error) {
            console.log("error", error)
            toast.error('Failed to add comment');
        }
    };

    const handleEditLyrics = (lyric) => {
        setIsEditingLyrics(true);
        setLyricsForEditing(lyric);
        setUploadLyricsPopup(true);
    };

    const showConfirmationPopup = (lyricId, lyricData) => {
        setConfirmationPopup({
            show: true,
            lyricId,
            lyricData
        });
    };

    const hideConfirmationPopup = () => {
        setConfirmationPopup({
            show: false,
            lyricId: null,
            lyricData: null
        });
    };

    const handleApproveLyric = async () => {
        try {
            const payload = {
                "order_id": orderId,
                "lyric_id": confirmationPopup.lyricId
            };

            const response = await dispatch(approveLyrics(payload)).unwrap();
            if (response.success) {
                await dispatch(getLyrics(orderId));
                toast.success('Lyrics approved!', {
                    duration: 2000,
                    position: 'top-right',
                });
                hideConfirmationPopup();
            }
        } catch (error) {
            console.log("error", error);
            toast.error('Failed to approve lyrics');
            hideConfirmationPopup();
        }
    };

    const handleCopyShareUrl = (url) => {
        if (!url) return;

        if (navigator.clipboard && window.isSecureContext) {
            navigator.clipboard.writeText(url)
                .then(() => {
                    toast.success("URL copied!");
                })
                .catch(() => {
                    fallbackCopy(url);
                });
        } else {
            fallbackCopy(url);
        }
    };

    const fallbackCopy = (text) => {
        const textArea = document.createElement("textarea");
        textArea.value = text;
        textArea.style.position = "fixed";
        textArea.style.opacity = "0";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();

        try {
            document.execCommand("copy");
            toast.success("URL copied!");
        } catch (err) {
            toast.error("Failed to copy");
        }

        document.body.removeChild(textArea);
    };

    if (!orderId) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
                <div className="bg-white rounded-lg border border-gray-200 p-4 max-w-sm w-full text-center">
                    <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <Lock className="w-6 h-6 text-red-500" />
                    </div>
                    <h2 className="text-base font-bold text-gray-900 mb-1.5">Order ID Required</h2>
                    <p className="text-gray-600 text-sm mb-3">Please check the URL</p>
                    <button
                        onClick={() => window.history.back()}
                        className="px-4 py-1.5 bg-orange-500 text-white text-sm font-medium rounded hover:bg-orange-600"
                    >
                        Go Back
                    </button>
                </div>
            </div>
        );
    }

    // Compact Confirmation Popup
    const ConfirmationPopup = () => {
        if (!confirmationPopup.show) return null;

        const lyric = confirmationPopup.lyricData;
        const previewText = lyric?.text?.length > 100
            ? lyric.text.substring(0, 100) + '...'
            : lyric.text;

        return (
            <div className="fixed inset-0 bg-black/40 backdrop-blur-sm bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-lg border border-gray-200 max-w-sm w-full">
                    <div className="p-4">
                        <div className="flex items-center gap-2.5 mb-3">
                            <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
                                <AlertTriangle className="w-5 h-5 text-yellow-600" />
                            </div>
                            <div>
                                <h3 className="text-base font-bold text-gray-900">Approve Lyrics</h3>
                                <p className="text-xs text-gray-600">Confirm action</p>
                            </div>
                        </div>

                        <div className="bg-gray-50 rounded border border-gray-200 p-3 mb-3">
                            <div className="flex items-center gap-2 mb-2">
                                <div className="w-6 h-6 bg-blue-100 rounded flex items-center justify-center">
                                    <span className="font-bold text-blue-600 text-xs">
                                        {items.findIndex(item => item.lyric_id === lyric?.lyric_id) + 1}
                                    </span>
                                </div>
                                <div>
                                    <h4 className="font-semibold text-gray-900 text-sm">Version {items.findIndex(item => item.lyric_id === lyric?.lyric_id) + 1}</h4>
                                    <p className="text-xs text-gray-500">
                                        By {lyric?.created_by?.split('@')[0]} • {moment(lyric?.created_at).format("MMM D")}
                                    </p>
                                </div>
                            </div>
                            <div className="mt-2">
                                <p className="text-xs text-gray-700 font-medium mb-1">Preview:</p>
                                <div className="bg-white border border-gray-200 rounded p-2 max-h-20 overflow-y-auto text-xs">
                                    {previewText}
                                </div>
                            </div>
                        </div>

                        <div className="bg-yellow-50 border border-yellow-200 rounded p-3 mb-3">
                            <div className="flex items-start gap-1.5">
                                <AlertTriangle className="w-3.5 h-3.5 text-yellow-600 mt-0.5" />
                                <div className="text-xs text-yellow-700">
                                    <p className="font-medium mb-0.5">Important</p>
                                    <ul className="space-y-0.5">
                                        <li>• Cannot be undone</li>
                                        <li>• Comments will be disabled</li>
                                        {approved_lyric_id && (
                                            <li className="font-medium">• Replaces current version</li>
                                        )}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="px-4 py-3 bg-gray-50 rounded-b-lg flex justify-end gap-2">
                        <button
                            onClick={hideConfirmationPopup}
                            className="px-3 py-1.5 border border-gray-300 text-gray-700 text-sm rounded hover:bg-gray-50"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleApproveLyric}
                            className="px-3 py-1.5 bg-green-500 text-white text-sm rounded hover:bg-green-600 flex items-center gap-1"
                        >
                            <CheckCircle className="w-3.5 h-3.5" />
                            Approve
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="min-h-screen bg-gray-50 p-2">
            <div className="max-w-7xl mx-auto">
                <div className='flex justify-center'>
                <img
                    src={digikoreLogo}
                    alt="Digikore Logo"
                    className="h-12 mb-6 w-48"
                />
                </div>
                {/* Header Section - Compact */}
                <div className="bg-white rounded-lg border border-gray-200 p-3 mb-3">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                            <div className="p-1 bg-orange-500 rounded">
                                <Music className="w-4 h-4 text-white" />
                            </div>
                            <div>
                                <h1 className="text-base font-bold text-gray-900">Lyrics</h1>
                                <div className="flex items-center gap-1">
                                    <span className="text-xs text-gray-500">Order:</span>
                                    <code className="font-mono bg-gray-100 px-1.5 py-0.5 rounded text-xs text-gray-800">
                                        {orderId}
                                    </code>
                                </div>
                            </div>
                        </div>

                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => handleCopyShareUrl(`${window.location.origin}/lyrics/${orderId}`)}
                                className="px-2 py-1 bg-orange-50 border border-orange-200 text-orange-700 rounded text-sm hover:bg-orange-100 flex items-center gap-1"
                            >
                                <Copy className="w-3.5 h-3.5" />
                                Copy URL
                            </button>
                            {/* Status Badge */}
                            {locked ? (
                                <div className="px-2 py-1 bg-green-500 text-white rounded text-sm flex items-center gap-1">
                                    <CheckCircle className="w-3.5 h-3.5" />
                                    <span>Approved</span>
                                </div>
                            ) : (
                                <div className="px-2 py-1 bg-blue-500 text-white rounded text-sm flex items-center gap-1">
                                    <div className="w-1.5 h-1.5 bg-white rounded-full animate-pulse"></div>
                                    <span>Review</span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Main Content */}
                {items.length === 0 ? (
                    <div className="bg-white rounded-lg border border-gray-200 p-4 text-center">
                        <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                            <FileText className="w-6 h-6 text-gray-400" />
                        </div>
                        <h2 className="text-sm font-bold text-gray-800 mb-1">No Lyrics</h2>
                        <p className="text-gray-600 text-xs">
                            No lyrics uploaded for this order
                        </p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {items.map((lyric, index) => {
                            const isApproved = lyric.lyric_id === approved_lyric_id;
                            const isLocked = locked && isApproved;

                            return (
                                <div
                                    key={lyric.lyric_id}
                                    className={`bg-white rounded-lg border ${isApproved ? 'border-green-500' : 'border-gray-200'} hover:border-gray-300`}
                                >
                                    {/* Header */}
                                    <div className={`p-2.5 ${isApproved ? 'bg-green-50' : 'bg-gray-50'}`}>
                                        <div className="flex justify-between items-start">
                                            <div className="flex items-center gap-2">
                                                <div className={`w-6 h-6 rounded flex items-center justify-center ${isApproved ? 'bg-green-100 text-green-600' : 'bg-gray-200 text-gray-600'
                                                    }`}>
                                                    <span className="font-bold text-xs">{index + 1}</span>
                                                </div>
                                                <div>
                                                    <h3 className="font-bold text-gray-900 text-sm">Version {index + 1}</h3>
                                                    <div className="flex items-center gap-1 text-xs text-gray-500">
                                                        <User className="w-3 h-3" />
                                                        <span>{lyric.created_by.split('@')[0]}</span>
                                                        <Calendar className="w-3 h-3 ml-1" />
                                                        <span>{moment(lyric?.created_at).format("MMM D")}</span>
                                                    </div>
                                                </div>
                                            </div>

                                            {isApproved && (
                                                <div className="px-1.5 py-0.5 bg-green-100 text-green-800 rounded-full flex items-center gap-1">
                                                    <CheckCircle className="w-3 h-3" />
                                                    <span className="font-medium text-xs">Approved</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Lyrics Content */}
                                    <div className="p-2.5">
                                        <div className="bg-gray-50 rounded border border-gray-200 p-2 mb-2 max-h-80 overflow-y-auto">
                                            <p className="text-gray-800 text-xs whitespace-pre-wrap leading-relaxed">{lyric.text}</p>
                                        </div>

                                        {/* Action Buttons */}
                                        <div className="flex flex-col gap-1.5 mb-2">
                                            {!locked && (
                                                <button
                                                    onClick={() => handleEditLyrics(lyric)}
                                                    className="w-full px-2 py-1.5 bg-orange-50 border border-orange-200 text-orange-700 rounded text-sm hover:bg-orange-100 flex items-center justify-center gap-1"
                                                >
                                                    <Edit className="w-3.5 h-3.5" />
                                                    Edit
                                                </button>
                                            )}

                                            {!locked && !isApproved && (
                                                <button
                                                    onClick={() => showConfirmationPopup(lyric.lyric_id, lyric)}
                                                    className="w-full px-2 py-1.5 bg-green-500 text-white rounded text-sm hover:bg-green-600 flex items-center justify-center gap-1"
                                                >
                                                    <CheckCircle className="w-3.5 h-3.5" />
                                                    Approve
                                                </button>
                                            )}
                                        </div>

                                        {/* Comments Section */}
                                        <div className="border-t pt-2">
                                            <div className="flex items-center gap-1 mb-1.5">
                                                <MessageSquare className="w-3.5 h-3.5 text-gray-400" />
                                                <h4 className="font-medium text-gray-900 text-xs">Comments ({lyric.comments?.length || 0})</h4>
                                            </div>

                                            {/* Comments List */}
                                            <div className="space-y-1.5 mb-1.5 max-h-16 overflow-y-auto pr-1">
                                                {lyric.comments?.length > 0 ? (
                                                    lyric.comments.map((comment, idx) => (
                                                        <div key={idx} className="bg-gray-50 rounded p-1.5">
                                                            <div className="flex items-start gap-1">
                                                                <div className="w-5 h-5 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0">
                                                                    <User className="w-2.5 h-2.5 text-gray-600" />
                                                                </div>
                                                                <div className="flex-1 min-w-0">
                                                                    <div className="flex items-center gap-0.5">
                                                                        <span className="text-xs text-gray-500">
                                                                            {moment(comment.commented_at).format("MMM D")}
                                                                        </span>
                                                                    </div>
                                                                    <p className="text-gray-700 text-xs mt-0.5">{comment.text}</p>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))
                                                ) : (
                                                    <div className="text-center py-1 text-gray-500 text-xs">
                                                        No comments
                                                    </div>
                                                )}
                                            </div>

                                            {/* Add Comment */}
                                            {!locked ? (
                                                <div className="mt-1.5">
                                                    {activeCommentBox === lyric.lyric_id ? (
                                                        <div className="space-y-1.5">
                                                            <textarea
                                                                value={newComment}
                                                                onChange={(e) => setNewComment(e.target.value)}
                                                                placeholder="Write comment..."
                                                                rows="2"
                                                                className="w-full border border-gray-300 rounded p-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-orange-500 resize-none"
                                                            />
                                                            <div className="flex justify-end gap-1">
                                                                <button
                                                                    onClick={() => {
                                                                        setActiveCommentBox(null);
                                                                        setNewComment('');
                                                                    }}
                                                                    className="px-2 py-1 border border-gray-300 text-gray-700 rounded text-xs hover:bg-gray-50"
                                                                >
                                                                    Cancel
                                                                </button>
                                                                <button
                                                                    onClick={() => handleSubmitComment(lyric.lyric_id)}
                                                                    disabled={!newComment.trim()}
                                                                    className="px-2 py-1 bg-orange-500 text-white rounded text-xs disabled:opacity-50 flex items-center gap-0.5"
                                                                >
                                                                    <Send className="w-3 h-3" />
                                                                    Post
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <button
                                                            onClick={() => setActiveCommentBox(lyric.lyric_id)}
                                                            className="w-full border border-dashed border-gray-300 rounded p-1.5 text-gray-500 hover:text-gray-700 hover:border-gray-400 text-xs"
                                                        >
                                                            + Add comment
                                                        </button>
                                                    )}
                                                </div>
                                            ) : (
                                                <div className="p-1.5 bg-gray-50 rounded text-center">
                                                    <div className="flex items-center justify-center gap-1 text-gray-500 text-xs">
                                                        <Lock className="w-3 h-3" />
                                                        <span>Comments locked</span>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Summary Footer */}
                {items.length > 0 && (
                    <div className="mt-3 bg-white rounded-lg border border-gray-200 p-3">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                            <div className="text-gray-600">
                                <h4 className="font-semibold text-gray-900 text-sm mb-0.5">Summary</h4>
                                <p className="text-xs">
                                    <span className="font-medium">{items.length} version{items.length !== 1 ? 's' : ''}</span>
                                    {approved_lyric_id && ' • 1 approved'}
                                </p>
                            </div>
                            <div className="text-xs text-gray-500">
                                <div className="flex items-center gap-1.5">
                                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                    <span>Approved</span>
                                    <div className="w-2 h-2 bg-blue-500 rounded-full ml-2"></div>
                                    <span>Pending</span>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Lyrics Upload Popup */}
            {uploadLyricsPopup && (
                <LyricsUploadPopup
                    uploadLyricsPopup={uploadLyricsPopup}
                    setUploadLyricsPopup={setUploadLyricsPopup}
                    orderId={orderId}
                    isEditingLyrics={isEditingLyrics}
                    setIsEditingLyrics={setIsEditingLyrics}
                    lyricsForEditing={lyricsForEditing}
                    setLyricsForEditing={setLyricsForEditing}
                    onAfterSubmit={async () => {
                        await dispatch(getLyrics(orderId));
                    }}
                />
            )}

            {/* Confirmation Popup */}
            <ConfirmationPopup />
        </div>
    );
};

export default ShareLyrics;